'use client';
import { PortableTextObject } from "sanity";
import { styles, MapVariants } from "./Map.css";
import { SanityAssetDocument, SanityImageAssetDocument } from "next-sanity";
import { MapSvg } from "./MapSvg/MapSvg";
import { AppList } from "@otomoro/components/shared/AppList";
import { LocationTab } from "./Location/LocationTab";
import { useState } from "react";
import clsx from "clsx";
import { LocationPopup } from "./Location/LocationPopup";
import { ImageSlider } from "@otomoro/components/shared/ImageSlider";
import { AnimatePresence, motion } from "framer-motion";
import { SectionHeader } from "@otomoro/components/shared/SectionHeader";

interface MapProps extends MapVariants {
  tag: string;
  headline?: PortableTextObject;
  locationsList: {
    country: string;
    city: string;
    thumbnail: SanityAssetDocument;
    content: PortableTextObject;
    location: string;
  }[];
  topImageSlider?: SanityImageAssetDocument[];
	sliderTag?: string;
	sliderHeadline?: PortableTextObject;
}

export const Map: React.FC<MapProps> = ({ sliderTag, sliderHeadline, tag, headline, locationsList, topImageSlider }) => {
  const [openIdx, setOpenIdx] = useState<number|null>(null);

  const toggleDetails = (e: React.MouseEvent<Element, MouseEvent> | React.FocusEvent, idx: number) => {
    e.preventDefault()
    if (openIdx === idx) {
      setOpenIdx(0)
    } else {
      setOpenIdx(idx)
    }
  }

  const { outerWrapper, outerHeader, base, wrapper, header, desktopList, mobileList, mapWrapper, marker, pin, slider, figure, image, slide } = styles();

  return (
		<div className={outerWrapper()}>
			{sliderHeadline && (
				<SectionHeader
					className={clsx(outerHeader(), '[&>h2]:lg:col-span-6')}
					tag={sliderTag}
					title={sliderHeadline as PortableTextObject}
					tagClass='mx-auto'
				/>
			)}
			<section className={base()}>
				{topImageSlider &&
					<ImageSlider
						className={slider()}
						slideClassName={slide()}
						imageClassName={image()}
						figureClassName={figure()}
						images={topImageSlider}
					/>
				}
				<div className={wrapper()}>
					<SectionHeader tag={tag} title={headline as PortableTextObject} theme="dark" noPx className={clsx(header(), '[&>h2]:lg:col-span-6')} tagClass='mx-auto' />
					<div className={mapWrapper()}>
						<MapSvg />

						{(locationsList && locationsList.length > 0) && (
							<AppList noWrapper className={desktopList()}>
								{locationsList.map((location, index) => (
									<li
										key={index}
										className={
											clsx(marker({ country: location.location as any }),
											{[marker({ country: location.location as any, active: true })]: openIdx === index})
										}
									>
										<span
											className={clsx(pin({
												country: location.location as any
											}),
												{
													[pin({active: true})]: openIdx === index
												}
											)}
											onMouseEnter={() => setOpenIdx(index)}
											onClick={() => setOpenIdx(index)}
											onFocus={() => setOpenIdx(index)}
											onMouseLeave={() => setOpenIdx(null)}
											aria-label={`${location.city}, ${location.location} details`}
											tabIndex={0}
										></span>

										<AnimatePresence>
											{(openIdx === index && window.matchMedia('(min-width: 1024px)').matches) && (
												<motion.div
													initial={{ opacity: 0 }}
													animate={{ opacity: 1 }}
													exit={{ opacity: 0 }}
												>
													<LocationPopup
														location={location.location}
														country={location.country}
														city={location.city}
														content={location.content}
														thumbnail={location.thumbnail}
													/>
												</motion.div>
											)}
										</AnimatePresence>
									</li>
								))}
							</AppList>
						)}
					</div>

					{/* Mobile layout */}
					{(locationsList && locationsList.length > 0) && (
						<AppList className={mobileList()}>
							{locationsList.map((location, idx) => (
								<LocationTab
									key={idx}
									index={idx}
									country={location.country}
									city={location.city}
									content={location.content}
									thumbnail={location.thumbnail}
									openIdx={openIdx}
									toggleDetails={toggleDetails}
								/>
							))}
						</AppList>
					)}
				</div>
			</section>
		</div>
  )
};
